import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { gapKeys, useGenerateForGap } from "@/api/jd-gap-queries";
import { useJob } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Input, Select } from "@/components/ui/input";
import { ErrorNote } from "@/components/ui/panel";

/**
 * Drafting questions for one JD's gap — DESIGN §20.6, Phase 3. Queues `jd_generate` (the ordinary
 * generate job with the skill as its focus); the drafts wait on the JD's Questions step for the Resource
 * Manager to keep or discard. A drawer shared by the Skills step and the gap report, so there is one way
 * to ask and one place the answer goes, and the job's progress stays in view until it is done.
 */

export interface GapSkill {
  jdId: number;
  skillId: number;
  name: string;
  /** "Must · 'GraphQL APIs (Hot Chocolate)' · not mapped" — who is asking and why. */
  context: string;
  stack: string | null;
  /** The stacks it may be filed under: technical ones for a technical skill, domains for a domain one. */
  stacks: string[];
}

function Form({ gap, maxPerRun }: { gap: GapSkill; maxPerRun: number }) {
  const generate = useGenerateForGap(gap.jdId);
  const [stack, setStack] = useState(gap.stack ?? "");
  const [count, setCount] = useState(10);
  const job = useJob(generate.data?.job_id ?? null);
  const client = useQueryClient();
  const state = job.data?.status;
  const writing = generate.isPending || state === "queued" || state === "running";

  // The drafts list is the JD's; tell it there is something new the moment the job is done.
  useEffect(() => {
    if (state === "done") void client.invalidateQueries({ queryKey: gapKeys.drafts(gap.jdId) });
  }, [state, gap.jdId, client]);

  const clamp = (n: number) => Math.max(1, Math.min(maxPerRun, Math.floor(n) || 1));

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-1 flex-col gap-4 p-4">
        <p className="text-[13px]">
          The AI writes draft questions about this skill.{" "}
          <span className="text-bank-muted">
            You keep or discard each draft; kept ones go through the pipeline and review like any other
            question, then serve this JD.
          </span>
        </p>
        <p className="bg-bank-surface-2 rounded-md px-2.5 py-2 text-[12.5px]">
          <span className="text-bank-muted mr-1.5 font-mono text-[10.5px] tracking-[0.07em] uppercase">
            Skill
          </span>
          <strong>{gap.name}</strong> <span className="text-bank-muted">— {gap.context}</span>
        </p>
        <div>
          <label htmlFor="gen-stack" className="mb-1 block text-[12.5px] font-medium">
            File them under
          </label>
          <Select id="gen-stack" value={stack} onChange={(e) => setStack(e.target.value)}>
            <option value="">The JD's main stack</option>
            {gap.stacks.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          {/* §20.17: a gap has no stack of its own; the stack chosen, else its own, else the JD's heaviest. */}
          <p className="text-bank-muted mt-1 text-xs">
            A gap has no stack of its own, so the drafts need a home in the bank.
          </p>
        </div>
        <div>
          <label htmlFor="gen-count" className="mb-1 block text-[12.5px] font-medium">
            How many
          </label>
          <div className="flex items-center gap-2.5">
            <div className="border-bank-border-strong bg-bank-surface inline-flex items-center rounded-md border">
              <Button size="sm" variant="ghost" aria-label="Fewer" onClick={() => setCount(clamp(count - 1))}>
                −
              </Button>
              <Input
                id="gen-count"
                type="number"
                min={1}
                max={maxPerRun}
                className="h-7 w-12 border-0 text-center font-mono"
                value={count}
                onChange={(e) => setCount(clamp(Number(e.target.value)))}
              />
              <Button size="sm" variant="ghost" aria-label="More" onClick={() => setCount(clamp(count + 1))}>
                +
              </Button>
            </div>
            <span className="text-bank-muted text-[12.5px]">up to {maxPerRun} · one AI request</span>
          </div>
        </div>
        <div>
          <Button
            variant="primary"
            disabled={writing}
            onClick={() => generate.mutate({ skill_id: gap.skillId, count, stack: stack || null })}
          >
            <Sparkles className="size-4" aria-hidden="true" />
            Draft {count} question{count === 1 ? "" : "s"}
          </Button>
        </div>
        <ErrorNote error={generate.error} />

        {(state === "queued" || state === "running") && (
          <p className="flex items-center gap-2 text-[13px]">
            <Loader2 className="text-bank-pipe size-4 animate-spin" aria-hidden="true" />
            Writing drafts… <Badge value={state} />
            <span className="text-bank-muted font-mono text-[11px]">job #{generate.data?.job_id}</span>
          </p>
        )}
        {state === "done" && (
          <div className="bg-bank-good-wash text-bank-good-ink flex flex-wrap items-center gap-2.5 rounded-md px-3 py-2.5 text-[13px]">
            <Check className="size-4" aria-hidden="true" />
            <span>
              <strong>The drafts are ready.</strong> Keep the good ones on the Questions step.
            </span>
            <Link
              to={`/jds/${gap.jdId}?step=questions`}
              className="border-bank-border-strong bg-bank-surface text-bank-ink hover:bg-bank-surface-2 ml-auto inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium"
            >
              Review drafts
            </Link>
          </div>
        )}
        {state === "failed" && (
          <p className="text-bank-bad-ink text-[13px]">{job.data?.error ?? "The job failed."}</p>
        )}
      </div>
      <p className="text-bank-muted border-bank-border border-t px-4 py-3 text-xs">
        You can close this — the job carries on and the drafts wait on the JD's Questions step.
      </p>
    </div>
  );
}

export function DraftQuestionsDrawer({
  gap,
  maxPerRun = 50,
  onClose,
}: {
  gap: GapSkill | null;
  maxPerRun?: number;
  onClose: () => void;
}) {
  return (
    <Drawer open={gap !== null} onClose={onClose} title={gap ? `Draft questions for ${gap.name}` : ""}>
      {gap && <Form key={`${gap.jdId}-${gap.skillId}`} gap={gap} maxPerRun={maxPerRun} />}
    </Drawer>
  );
}
